import React, {
  ChangeEvent,
  FC,
  PropsWithChildren,
  useContext,
  useState,
} from 'react';
import {FormState, InfoBoxType, LanguageContext, Settings} from './app';
import {Mode, Operation} from './math';
import {InfoIcon} from '@primer/octicons-react';
import {i18n} from './i18n';

export interface SettingsProps {
  readonly settings: Settings;
  readonly setSettings: (settings: Settings) => void;
  readonly formState: FormState;
  readonly setFormState: (formState: FormState) => void;
  readonly setInfoBoxType: React.Dispatch<
    React.SetStateAction<InfoBoxType | undefined>
  >;
}

export const SettingsComponent: FC<PropsWithChildren<SettingsProps>> = ({
  settings,
  setSettings,
  formState,
  setFormState,
  setInfoBoxType,
}) => {
  const [localSettings, setLocalSettings] = useState<Settings>(settings);

  const language = useContext(LanguageContext);
  const translations = i18n[language];

  const onChangeOperation = (e: ChangeEvent<HTMLInputElement>) => {
    setLocalSettings({
      ...localSettings,
      operation: e.target.value as Operation,
    });
    setSettings({...localSettings, operation: e.target.value as Operation});
  };

  const onChangeMode = (e: ChangeEvent<HTMLInputElement>) => {
    setLocalSettings({...localSettings, mode: e.target.value as Mode});
  };

  const onChangeEntriesAmount = (e: ChangeEvent<HTMLInputElement>) => {
    setLocalSettings({...localSettings, entriesAmount: e.target.valueAsNumber});
  };

  const onChangeBaseFactor = (e: ChangeEvent<HTMLInputElement>) => {
    setLocalSettings({...localSettings, baseFactor: e.target.valueAsNumber});
  };

  const onChangeRange = (e: ChangeEvent<HTMLInputElement>) => {
    setLocalSettings({
      ...localSettings,
      range: {...settings.range, max: e.target.valueAsNumber},
    });
  };

  function renderSetting(
    title: string,
    setting: JSX.Element,
    infoBoxType?: InfoBoxType
  ) {
    return (
      <div className={'row-auto inline-grid gap-1 pr-8 pb-6'}>
        <b>
          {title}
          {infoBoxType && (
            <span
              className={'cursor-pointer'}
              onClick={() => setInfoBoxType(infoBoxType)}
            >
              &nbsp; <InfoIcon size={'small'} />
            </span>
          )}
        </b>

        {setting}
      </div>
    );
  }

  function renderOperation() {
    const setting = (
      <div
        className={'x- inline-grid grid-cols-4 gap-2'}
        onChange={onChangeOperation}
      >
        <label>
          <input
            type="radio"
            value="add"
            name="operation"
            checked={localSettings.operation === 'add'}
            onChange={(e) => onChangeOperation(e)}
          />{' '}
          {translations.settings.operation.add}
        </label>
        <label>
          <input
            type="radio"
            value="subtract"
            name="operation"
            checked={localSettings.operation === 'subtract'}
            onChange={(e) => onChangeOperation(e)}
          />{' '}
          {translations.settings.operation.substract}
        </label>
        <label>
          <input
            type="radio"
            value="multiply"
            name="operation"
            checked={localSettings.operation === 'multiply'}
            onChange={(e) => onChangeOperation(e)}
          />{' '}
          {translations.settings.operation.multiply}
        </label>
        <label>
          <input
            type="radio"
            value="divide"
            name="operation"
            checked={localSettings.operation === 'divide'}
            onChange={(e) => onChangeOperation(e)}
          />{' '}
          {translations.settings.operation.divide}
        </label>
      </div>
    );

    return renderSetting(translations.settings.operation.title, setting);
  }

  function renderMode() {
    const setting = (
      <div
        className={'x- inline-grid grid-cols-2 gap-2'}
        onChange={onChangeMode}
      >
        <label>
          <input
            type="radio"
            value="result"
            name="mode"
            checked={localSettings.mode === 'result'}
            onChange={(e) => onChangeMode(e)}
          />{' '}
          {translations.settings.mode.normal}
        </label>
        <label>
          <input
            type="radio"
            value="part"
            name="mode"
            checked={localSettings.mode === 'part'}
            onChange={(e) => onChangeMode(e)}
          />{' '}
          {translations.settings.mode.hard}
        </label>
      </div>
    );

    return renderSetting(translations.settings.mode.title, setting, 'mode');
  }

  function renderAmountOfEntries() {
    const setting = (
      <div className={'mx-0 my-auto flex items-center justify-between'}>
        <input
          type="range"
          min="10"
          max="50"
          step={5}
          value={localSettings.entriesAmount}
          onChange={(e) => onChangeEntriesAmount(e)}
          className="h-2 w-full min-w-[250px] cursor-pointer appearance-none  rounded-lg bg-gray-200 dark:bg-gray-700"
        />
        <span className={'pl-4'}> {localSettings.entriesAmount}</span>
      </div>
    );

    return renderSetting(translations.settings.amountOfEntries.title, setting);
  }

  function renderMaxNumberRange() {
    const setting = (
      <div className={'mx-0 my-auto flex items-center justify-between'}>
        <input
          type="range"
          min="10"
          step={10}
          max="100"
          value={localSettings.range.max}
          onChange={(e) => onChangeRange(e)}
          className="h-2 w-full  min-w-[250px]  cursor-pointer  appearance-none rounded-lg bg-gray-200 dark:bg-gray-700"
        />
        <span className={'pl-4'}> {localSettings.range.max}</span>
      </div>
    );

    return renderSetting(translations.settings.range.title, setting, 'range');
  }

  function renderMultiplyFactor() {
    const setting = (
      <input
        min={1}
        max={9}
        className={' h-8 w-8 text-center'}
        type="number"
        value={localSettings.baseFactor}
        onChange={(e) => onChangeBaseFactor(e)}
      />
    );

    return renderSetting(
      translations.settings.baseFactor.title,
      setting,
      'baseFactor'
    );
  }

  return (
    <div className={'pt-4 pb-6'}>
      <div className={'flex flex-row flex-wrap'}>
        {renderOperation()}
        {renderMode()}
        {renderAmountOfEntries()}
        {renderMaxNumberRange()}
        {(settings.operation === 'multiply' ||
          settings.operation === 'divide') &&
          renderMultiplyFactor()}
      </div>
      <div className={'flex content-center justify-center'}>
        <button
          type="submit"
          className="text-gray border-nonehover:opacity-50 rounded"
          onClick={() => {
            setFormState({
              ...formState,
              regenerateToggle: !formState.regenerateToggle,
              clearFormToggle: !formState.clearFormToggle,
            });
            setSettings(localSettings);
          }}
        >
          {translations.buttons.generate}
        </button>
      </div>
    </div>
  );
};
